/**
 * @file 相关函数
 */
/**
 * Get version precisions count
 *
 * @example
 *   getVersionPrecision("1.10.3") // 3
 * @param version - version
 * @returns version precision
 */
function getVersionPrecision(version) {
    return version.split('.').length;
}
/**
 * get browser recommendation
 *
 * @param ua - user agent string
 * @returns browser recommendation
 */
function getBrowserRecommendation(ua) {
    const { name: browserName } = ua.browser;
    const deviceName = ua.device;
    const { name: osName } = ua.os;
    if (deviceName === 'phone' || deviceName === 'tablet') {
        if (osName === 'iOS') {
            if (browserName === 'WeChat' || browserName === 'Safari') {
                return '请升级设备的系统版本。';
            }
            return '推荐使用最新版的谷歌浏览器或 QQ 浏览器，请到 App Store 下载。';
        }
        if (osName === 'Android' && browserName === 'WeChat') {
            return '请到应用商店升级微信至最新版。';
        }
        return '推荐使用最新版的谷歌浏览器或 QQ 浏览器，请到应用商店下载。';
    }
    let browserRecommendation = '';
    if (browserName === 'Safari') {
        browserRecommendation += `
      请升级你的操作系统版本
      <br />
      或者
      <br />
    `;
    }
    else if (browserName !== 'InternetExplorer' && browserName !== 'Chrome') {
        browserRecommendation += `
      请升级你的<a style="color: #4482e8;margin-left: 5px;" target="_blank" href="https://www.baidu.com/s?wd=${browserName}"> ${browserName} 浏览器</a>
      <br />
      或者
      <br />
    `;
    }
    browserRecommendation += `
    你可以试试<a style="color: #4482e8;margin-left: 5px;" target="_blank" href="https://www.baidu.com/s?wd=chrome">谷歌浏览器</a>。
    <br />
  `;
    return browserRecommendation;
}
/**
 * Get first matched item for a string
 *
 * @param regexp - regexp
 * @param ua - user agent string
 * @returns first matched item
 */
export function getFirstMatch(regexp, ua) {
    const match = ua.match(regexp);
    return (match && match.length > 0 && match[1]) || '';
}
/**
 * Get second matched item for a string
 *
 * @param regexp - regexp
 * @param ua - user agent string
 * @returns second matched item
 */
export function getSecondMatch(regexp, ua) {
    const match = ua.match(regexp);
    return (match && match.length > 1 && match[2]) || '';
}
/**
 * The browser is android but isn't like android
 *
 * @param ua - user agent string
 * @returns if it's android
 */
export function isAndroid(ua) {
    const notLikeAndroid = !/like android/i.test(ua);
    const butAndroid = /android/i.test(ua);
    return notLikeAndroid && butAndroid;
}
/**
 * Calculate browser version weight
 *
 * @example
 *   compareVersions('1.10.2.1',  '1.8.2.1.90')    // 1
 *   compareVersions('1.010.2.1', '1.09.2.1.90');  // 1
 *   compareVersions('1.10.2.1',  '1.10.2.1');     // 0
 *   compareVersions('1.10.2.1',  '1.0800.2');     // -1
 *   compareVersions('1.10.2.1',  '1.10',  true);  // 0
 *
 * @param versionA - versions versions to compare
 * @param versionB - versions versions to compare
 * @param isLoose - enable loose comparison
 * @returns comparison result: -1 when versionA is lower,
 * 1 when versionA is bigger, 0 when both equal
 */
export function compareVersions(versionA, versionB, isLoose = false) {
    // 1) get common precision for both versions, for example for "10.0" and "9" it should be 2
    const versionAPrecision = getVersionPrecision(versionA);
    const versionBPrecision = getVersionPrecision(versionB);
    let precision = Math.max(versionAPrecision, versionBPrecision);
    let lastPrecision = 0;
    const chunks = [versionA, versionB].map(version => {
        const delta = precision - getVersionPrecision(version);
        // 2) "9" -> "9.0" (for precision = 2)
        const _version = version + new Array(delta + 1).join('.0');
        // 3) "9.0" -> ["000000000", "000000009"]
        return _version
            .split('.')
            .map(chunk => new Array(20 - chunk.length).join('0') + chunk)
            .reverse();
    });
    // adjust precision for loose comparison
    if (isLoose) {
        lastPrecision = precision - Math.min(versionAPrecision, versionBPrecision);
    }
    // iterate in reverse order by reversed chunks array
    precision -= 1;
    while (precision >= lastPrecision) {
        // 4) compare: "000000009" > "000000010" = false (but "9" > "10" = true)
        if (chunks[0][precision] > chunks[1][precision]) {
            return 1;
        }
        if (chunks[0][precision] === chunks[1][precision]) {
            if (precision === lastPrecision) {
                // all version chunks are same
                return 0;
            }
            precision -= 1;
        }
        else if (chunks[0][precision] < chunks[1][precision]) {
            return -1;
        }
    }
    return 0;
}
/**
 * get the incompatible html string
 *
 * @param  userAgent - user agent string
 * @returns html
 */
export function getIncompatibleHtml(userAgent) {
    const desktopHtml = `
    <div style="font-weight: 600; position: relative; font-size: 14px;display: flex; width: 680px; margin: 120px auto;font-family: Helvetica Neue,Roboto,Arial,PingFang SC,Source Han Sans SC,Microsoft YaHei,SimSun,sans-serif;">
      <img style="position: absolute;z-index: -1; left: 0; top: 0; width: 371.5px; height: 268px;" src="http://seiue-chalkng.oss-cn-qingdao.aliyuncs.com/misc_assets/seiue_user_agent/incompatible_desktop.png"/>
      <div style="flex: 0 0 256px;"></div>
      <div style="text-align: left; flex: 1; color: #61646E;">
        <h1 style="font-size: 24px; line-height: 34px;">
          对不起，
          <br/>
          我们暂不支持您当前使用的浏览器。
        </h1>
        <div style="margin-top: 30px;">
          <p style="font-size: 16px; line-height: 22px">
            您当前的浏览器 ${`${userAgent.browser.name} ${userAgent.browser.version}`}
            <br />
            将会在系统中遇到兼容性问题。
          </p>
          <p style="font-weight: 400; font-size: 14px; line-height: 20px; margin-top: 15px">
            ${getBrowserRecommendation(userAgent)}
          </p>
        </div>
      </div>
    </div>
  `;
    const mobileHtml = `
    <div style="font-weight: 600; color: #61646E;position: relative; font-size: 14px; padding: 35px; margin: 120px auto;font-family: Helvetica Neue,Roboto,Arial,PingFang SC,Source Han Sans SC,Microsoft YaHei,SimSun,sans-serif;">
      <div style="background-image: url(http://seiue-chalkng.oss-cn-qingdao.aliyuncs.com/misc_assets/seiue_user_agent/incompatible_mobile.png);background-repeat: no-repeat;background-size: 100%;padding: 80px 5%">
        <h1 style="font-size: 22px; line-height: 30px;">
          对不起，
          <br/>
          我们暂不支持您当前使用的浏览器。
        </h1>
        <div style="margin-top: 10px;">
          <p style="font-size: 13px; line-height: 18px">
            您当前的浏览器 ${`${userAgent.browser.name} ${userAgent.browser.version}`}
            <br />
            将会在系统中遇到兼容性问题。
          </p>
          <p style="font-size: 12px; line-height: 17px; margin-top: 23px">
            ${getBrowserRecommendation(userAgent)}
          </p>
        </div>
      </div>
    </div>
  `;
    return userAgent.device === 'phone' ? mobileHtml : desktopHtml;
}
//# sourceMappingURL=data:application/json;base64,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